import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { StringObject } from "../../objects/StringObject";
import { styleTxn } from "../../utils/style-txn";

export class StringMagnetic extends StringModule {
  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "magnetic";
    this._type = 2; // UI Module

    this.cssProperties = [
      { name: "--magnetic-x", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--magnetic-y", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--magnetic-target-x", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--magnetic-target-y", syntax: "<number>", initialValue: "0", inherits: true }
    ];

    this.permissions.mobile.rebuild.height = false;
    this.permissions.mobile.rebuild.width = false;
    this.permissions.mobile.rebuild.scrollHeight = false;

    this.attributesToMap = [
      ...this.attributesToMap,
      { key: "strength", type: "number", fallback: this.settings["strength"] },
      { key: "radius", type: "number", fallback: this.settings["radius"] },
    ];
  }

  override initializeObject(
    globalId: number,
    object: StringObject,
    element: HTMLElement,
    attributes: Record<string, any>
  ): void {
    super.initializeObject(globalId, object, element, attributes);
    object.setProperty("magnetic-active", false);
    object.setProperty("magnetic-target-x", 0);
    object.setProperty("magnetic-target-y", 0);
    object.setProperty("magnetic-x", 0);
    object.setProperty("magnetic-y", 0);
    object.setProperty("magnetic-lerp", 0.1);
    object.setProperty("event-magnetic-name", this.getObjectEventName(object, "magnetic:move"));
    object.setProperty("__next-magnetic-x", 0);
    object.setProperty("__next-magnetic-y", 0);
    object.setProperty("__needs-magnetic-update", false);
  }

  onMouseMove(e: MouseEvent): void {
    for (let i = 0; i < this.objects.length; i++) {
      const object = this.objects[i];
      const element = object.htmlElement as HTMLElement;
      const rect = element.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const dx = e.clientX - centerX;
      const dy = e.clientY - centerY;
      const distance = Math.sqrt(dx ** 2 + dy ** 2);

      const radius = object.getProperty<number>("radius") ?? 0;
      const strength = object.getProperty<number>("strength") ?? 0;

      const factor = this.tools.magneticPull.process({
        distance,
        radius,
        strength,
      });

      object.setProperty("magnetic-target-x", dx * factor);
      object.setProperty("magnetic-target-y", dy * factor);
      if (factor > 0) {
        object.setProperty("magnetic-active", true);
      }
    }
  }

  onFrame(data: StringData): void {
    for (let i = 0; i < this.objects.length; i++) {
      const object = this.objects[i];
      if (object.getProperty<boolean>("magnetic-active") === true) {
        let magneticX = object.getProperty<number>("magnetic-x") ?? 0;
        let magneticY = object.getProperty<number>("magnetic-y") ?? 0;

        let lerp = object.getProperty<number>("magnetic-lerp") ?? 0.1;

        let targetMagneticX = object.getProperty<number>("magnetic-target-x") ?? 0;
        let targetMagneticY = object.getProperty<number>("magnetic-target-y") ?? 0;

        let lerpX = this.tools.lerp.process({
          from: magneticX,
          to: targetMagneticX,
          progress: lerp,
        });
        let lerpY = this.tools.lerp.process({
          from: magneticY,
          to: targetMagneticY,
          progress: lerp,
        });

        if (lerpX > -0.01 && lerpX < 0.01) {
          lerpX = 0;
          object.setProperty("magnetic-x", targetMagneticX);
        }
        if (lerpY > -0.01 && lerpY < 0.01) {
          lerpY = 0;
          object.setProperty("magnetic-y", targetMagneticY);
        }
        magneticX += lerpX;
        magneticY += lerpY;
        object.setProperty("magnetic-x", magneticX);
        object.setProperty("magnetic-y", magneticY);

        const eventName = object.getProperty<string>("event-magnetic-name");
        if (eventName) {
          this.events.emit(eventName, {
            x: magneticX,
            y: magneticY,
          });
        }
        object.setProperty("__next-magnetic-x", magneticX);
        object.setProperty("__next-magnetic-y", magneticY);
        object.setProperty("__needs-magnetic-update", true);

        if (targetMagneticX === magneticX && targetMagneticY === magneticY) {
          object.setProperty("magnetic-active", false);
        }
      }
    }
  }

  override onMutate(): void {
    for (let i = 0; i < this.objects.length; i++) {
      const object = this.objects[i];
      const needsUpdate = object.getProperty<boolean>("__needs-magnetic-update") === true;
      if (!needsUpdate) continue;

      const x =
        object.getProperty<number>("__next-magnetic-x") ??
        object.getProperty<number>("magnetic-x") ??
        0;
      const y =
        object.getProperty<number>("__next-magnetic-y") ??
        object.getProperty<number>("magnetic-y") ??
        0;

      this.applyToElementAndConnects(object, (el) => {
        styleTxn.setVar(el, "--magnetic-x", x);
        styleTxn.setVar(el, "--magnetic-y", y);
      });

      object.setProperty("__needs-magnetic-update", false);
    }
  }
}
